import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Table, Button, Spinner, Badge, Modal, Form, Row, Col, Alert } from 'react-bootstrap';
import { FiPlus, FiEdit, FiDownload, FiTrash2, FiBriefcase } from 'react-icons/fi';
import portfoliosService, { Portfolio, PortfolioSummary } from '../../services/portfolios.service';
import { toast } from 'react-toastify';

const emptyForm: Partial<Portfolio> = {
  code: '', name: '', client_entity: '', contact_name: '', contact_email: '', contact_phone: '',
  billing_trigger: '', billing_criteria: '', color: '#3B82F6', notes: '', is_active: true
};

const PortfoliosList: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<PortfolioSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Portfolio | null>(null);
  const [form, setForm] = useState<Partial<Portfolio>>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      setRows(await portfoliosService.summary());
    } catch {
      toast.error('Error cargando carteras');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setError(''); setShowModal(true); };
  const openEdit = (p: PortfolioSummary) => { setEditing(p); setForm({ ...p }); setError(''); setShowModal(true); };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setError('');
    if (!form.name || !form.name.trim()) { setError('El nombre es obligatorio'); return; }
    setSaving(true);
    try {
      if (editing) {
        await portfoliosService.update(editing.id, form);
        toast.success('Cartera actualizada');
      } else {
        await portfoliosService.create(form);
        toast.success('Cartera creada');
      }
      setShowModal(false);
      await load();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async (p: PortfolioSummary) => {
    if (!window.confirm(`¿Desactivar la cartera "${p.name}"? Los casos existentes se mantienen.`)) return;
    try {
      await portfoliosService.remove(p.id);
      toast.success('Cartera desactivada');
      await load();
    } catch { toast.error('Error desactivando'); }
  };

  const handleExport = async (id: number | 'all') => {
    try {
      await portfoliosService.downloadExcel(id);
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Error generando Excel');
    }
  };

  const fmtMoney = (n: number) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(n) || 0);

  return (
    <div className="portfolios-list">
      <div className="page-header">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h4 className="page-title mb-1"><FiBriefcase className="me-2" />Carteras / Portafolios</h4>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><Link to="/">Inicio</Link></li>
                <li className="breadcrumb-item active">Carteras</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex gap-2">
            <Button variant="success" onClick={() => handleExport('all')}>
              <FiDownload className="me-2" />Excel (todas)
            </Button>
            <Button variant="primary" onClick={openNew}>
              <FiPlus className="me-2" />Nueva Cartera
            </Button>
          </div>
        </div>
      </div>

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" /></div>
          ) : (
            <Table hover responsive className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th>Cartera</th>
                  <th>Entidad</th>
                  <th className="text-end">Casos</th>
                  <th className="text-end">Activos</th>
                  <th className="text-end">Cerrados</th>
                  <th className="text-end">Capital</th>
                  <th className="text-center">🟢🟡🔴⚪</th>
                  <th>Facturación</th>
                  <th className="text-end" style={{ width: 180 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-4 text-muted">No hay carteras registradas</td></tr>
                )}
                {rows.map(p => (
                  <tr key={p.id}>
                    <td>
                      <div className="fw-semibold">
                        <span style={{ display: 'inline-block', width: 12, height: 12, background: p.color || '#3B82F6', borderRadius: 3, marginRight: 8 }} />
                        {p.name}
                      </div>
                      <small className="text-muted">{p.code}</small>
                    </td>
                    <td><small>{p.client_entity || '-'}</small></td>
                    <td className="text-end fw-semibold">{p.cases_total}</td>
                    <td className="text-end">{p.cases_active}</td>
                    <td className="text-end">{p.cases_closed}</td>
                    <td className="text-end">{fmtMoney(p.total_amount)}</td>
                    <td className="text-center">
                      <Badge bg="success" className="me-1">{p.green}</Badge>
                      <Badge bg="warning" text="dark" className="me-1">{p.yellow}</Badge>
                      <Badge bg="danger" className="me-1">{p.red}</Badge>
                      <Badge bg="secondary">{p.gray}</Badge>
                    </td>
                    <td><small>{p.billing_trigger || '-'}</small></td>
                    <td className="text-end">
                      <Button size="sm" variant="light" title="Ver casos" onClick={() => navigate(`/cases?portfolio_id=${p.id}`)}>
                        <FiBriefcase />
                      </Button>{' '}
                      <Button size="sm" variant="success" title="Descargar Excel" onClick={() => handleExport(p.id)}>
                        <FiDownload />
                      </Button>{' '}
                      <Button size="sm" variant="light" title="Editar" onClick={() => openEdit(p)}>
                        <FiEdit />
                      </Button>{' '}
                      <Button size="sm" variant="outline-danger" title="Desactivar" onClick={() => handleDelete(p)}>
                        <FiTrash2 />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Editar Cartera' : 'Nueva Cartera'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Nombre *</Form.Label>
                <Form.Control name="name" value={form.name || ''} onChange={handleChange} required />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Código {editing ? '' : '(auto si vacío)'}</Form.Label>
                <Form.Control name="code" value={form.code || ''} onChange={handleChange} placeholder="Ej: SERFINANZA" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Entidad Cliente</Form.Label>
                <Form.Control name="client_entity" value={form.client_entity || ''} onChange={handleChange} placeholder="Ej: Banco Serfinanza" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Contacto Nombre</Form.Label>
                <Form.Control name="contact_name" value={form.contact_name || ''} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Contacto Email</Form.Label>
                <Form.Control type="email" name="contact_email" value={form.contact_email || ''} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Contacto Teléfono</Form.Label>
                <Form.Control name="contact_phone" value={form.contact_phone || ''} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Disparador de facturación</Form.Label>
                <Form.Select name="billing_trigger" value={form.billing_trigger || ''} onChange={handleChange}>
                  <option value="">Seleccionar...</option>
                  <option value="AL_PRESENTAR">Al presentar la demanda</option>
                  <option value="AL_NOTIFICAR">Al notificar al demandado</option>
                  <option value="AL_OBTENER_SENTENCIA">Al obtener sentencia</option>
                  <option value="MENSUAL">Mensual</option>
                  <option value="POR_ETAPA">Por etapa procesal</option>
                  <option value="OTRO">Otro</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Color</Form.Label>
                <Form.Control type="color" name="color" value={form.color || '#3B82F6'} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Criterio de facturación</Form.Label>
                <Form.Control as="textarea" rows={2} name="billing_criteria" value={form.billing_criteria || ''} onChange={handleChange}
                  placeholder="Ej: Serfinanza paga honorarios solo al obtener sentencia." />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Notas</Form.Label>
                <Form.Control as="textarea" rows={2} name="notes" value={form.notes || ''} onChange={handleChange} />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" animation="border" className="me-2" /> : null}
            {editing ? 'Guardar cambios' : 'Crear cartera'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PortfoliosList;
